// src/components/auth/LoginForm.jsx
// =============================================
// LoginForm.jsx
// Handles user sign-in, prioritizing Google Sign-In for a faster user experience.
// =============================================

import React, { useState, useEffect } from 'react';
import { Mail, Lock, AlertCircle } from 'lucide-react';
import { authManager } from '@/managers/AuthManager';
import toast from 'react-hot-toast';

export function LoginForm({ onModeChange }) {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [emailLoading, setEmailLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [authState, setAuthState] = useState(() => authManager.getState());

    useEffect(() => {
        const unsubscribe = authManager.subscribe(state => {
            setAuthState(state);
            setLoading(state.isAuthenticating);
            if (!state.isAuthenticating) {
                setEmailLoading(false);
                setGoogleLoading(false);
            }
        });
        return () => unsubscribe();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setEmailLoading(true);
        try {
            await authManager.signInWithEmail(formData.email, formData.password);
            window.location.href = '/'; 
        } catch (err) {
            setError(formatAuthError(err.message));
            setEmailLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setError(null);
        setGoogleLoading(true);
        try {
            await authManager.signInWithGoogle();
            window.location.href = '/';
        } catch (err) {
            setError(formatAuthError(err.message));
            setGoogleLoading(false);
        }
    };
    
    const handleForgotPassword = async () => {
        if (!formData.email) {
            setError('Please enter your email address to reset your password.');
            return;
        }
        setError(null);
        setLoading(true);
        try {
            await authManager.sendPasswordResetEmail(formData.email);
            toast.success('Password reset email sent! Please check your inbox.');
        } catch (err) {
            setError(formatAuthError(err.message));
        } finally {
            setLoading(false);
        }
    };

    const formatAuthError = (message) => {
        const errorMap = {
            'auth/user-not-found': 'No account found with this email. Please sign up.',
            'auth/wrong-password': 'Incorrect password. Please try again.',
            'auth/invalid-email': 'Please enter a valid email address.',
            'auth/network-request-failed': 'Network error. Please check your connection.',
            'auth/popup-closed-by-user': 'Sign in cancelled. Please try again.',
            'auth/too-many-requests': 'Access to this account has been temporarily disabled due to many failed login attempts. You can immediately restore it by resetting your password or you can try again later.'
        };
        const errorCode = message.match(/auth\/[\w-]+/)?.[0];
        return errorMap[errorCode] || 'An error occurred. Please try again.';
    };

    return (
        <div className={`space-y-4 transition-opacity duration-200
                      ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
            {error && (
                <div className="p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <p>{error}</p>
                </div>
            )}

            {/* Google Sign-In (Primary) */}
            <div className="pt-2">
                <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                    className="w-full py-2.5 px-4 bg-surface/60 backdrop-blur-sm border border-white/10 rounded-lg
                                hover:bg-surface/80 
                                text-sm text-on-surface font-medium
                                transition-all duration-200
                                focus:outline-none focus:ring-2 focus:ring-primary/20
                                disabled:opacity-50 disabled:cursor-not-allowed
                                flex items-center justify-center space-x-2 relative"
                >
                    <span className={`flex items-center justify-center gap-2
                                    transition-opacity duration-200
                                    ${googleLoading ? 'opacity-0' : 'opacity-100'}`}>
                        <svg className="w-4 h-4" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        <span>Sign In with Google</span>
                    </span>
                    {googleLoading && (
                        <span className="absolute inset-0 flex items-center justify-center">
                            <div className="w-4 h-4 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                        </span>
                    )}
                </button>
            </div>

            {/* Divider */}
            <div className="relative pt-2">
                <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border-color"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                    <span className="px-2 bg-background text-on-surface-dim">Or with Email & Password</span>
                </div>
            </div>

            {/* Email & Password Form (Secondary) */}
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-on-surface mb-1.5 sr-only">
                        Email
                    </label>
                    <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-on-surface-dim w-4 h-4" />
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                            className={`w-full pl-9 pr-4 py-2.5 bg-surface/60 backdrop-blur-sm rounded-lg
                                    text-sm text-on-surface placeholder:text-on-surface-dim/50
                                    border border-white/10 focus:border-primary/50
                                    focus:outline-none focus:ring-2 focus:ring-primary/20
                                    transition-all duration-200
                                    ${loading ? 'cursor-not-allowed' : ''}`}
                            placeholder="name@youremail.com"
                            required
                            autoComplete="email"
                            disabled={loading}
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-on-surface mb-1.5 sr-only">
                        Password
                    </label>
                    <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-on-surface-dim w-4 h-4" />
                        <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                            className={`w-full pl-9 pr-4 py-2.5 bg-surface/60 backdrop-blur-sm rounded-lg
                                    text-sm text-on-surface placeholder:text-on-surface-dim/50
                                    border border-white/10 focus:border-primary/50
                                    focus:outline-none focus:ring-2 focus:ring-primary/20
                                    transition-all duration-200
                                    ${loading ? 'cursor-not-allowed' : ''}`}
                            placeholder="••••••••"
                            required
                            autoComplete="current-password"
                            disabled={loading}
                        />
                    </div>
                </div>
                
                <div className="flex items-center justify-end">
                    <button
                        type="button"
                        onClick={handleForgotPassword}
                        disabled={loading}
                        className="text-xs text-primary-light hover:text-primary transition-colors font-medium"
                    >
                        Forgot Password?
                    </button>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2.5 px-4 bg-gradient-primary
                            hover:opacity-90
                            rounded-lg text-sm text-white font-medium
                            transform transition-all duration-200
                            hover:shadow-button active:scale-[0.98]
                            focus:outline-none focus:ring-2 focus:ring-primary/30
                            disabled:opacity-50 disabled:cursor-not-allowed
                            relative"
                >
                    <span className={`flex items-center justify-center gap-2
                                    transition-opacity duration-200
                                    ${emailLoading ? 'opacity-0' : 'opacity-100'}`}>
                        Sign In
                    </span>
                    {emailLoading && (
                        <span className="absolute inset-0 flex items-center justify-center">
                            <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                            <span className="ml-2 text-sm">Signing in...</span>
                        </span>
                    )}
                </button>
            </form>

            {/* Footer Link */}
            <p className="text-center text-sm text-on-surface-dim pt-2">
                Don't have an account?{' '}
                <button
                    type="button"
                    onClick={onModeChange}
                    className="text-primary-light hover:text-primary transition-colors font-medium"
                    disabled={loading}
                >
                    Sign up
                </button>
            </p>
        </div>
    );
}

export default LoginForm;